import React, { useState } from "react";
import axios from "axios";
import { X, CheckCircle2, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { indianStates, billRanges, company } from "../mock";
import { useContent } from "../contexts/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnquireModal({ open, onClose }) {
  const { content } = useContent();
  const states = content.indianStates || indianStates;
  const bills = content.billRanges || billRanges;
  const co = content.company || company;
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    state: "",
    bill: "",
  });
  const [done, setDone] = useState(false);
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.phone || !form.state) return;
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        email: form.email,
        state: form.state,
        bill: form.bill,
        source: "enquire_modal",
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or call us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setDone(false);
    setForm({ firstName: "", lastName: "", phone: "", email: "", state: "", bill: "" });
    setAgree(false);
    setError("");
    setSubmitting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={close}
      />
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <button
          onClick={close}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center z-10 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {done ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h3 className="mt-5 text-2xl font-bold text-slate-900">Thank you!</h3>
            <p className="mt-2 text-slate-600">
              Our solar expert will call you back within 2 working hours on <span className="font-semibold text-slate-900">{form.phone}</span>.
            </p>
            <Button
              onClick={close}
              className="mt-6 bg-[#0a4f47] hover:bg-[#0e6e63] text-white h-11 px-6 rounded-md font-semibold"
            >
              Continue browsing
            </Button>
          </div>
        ) : (
          <div>
            <div className="bg-[#0a4f47] text-white px-6 py-5">
              <h3 className="text-2xl font-extrabold">Contact Us</h3>
              <p className="text-emerald-100 text-sm mt-1">Send us your enquiries here</p>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  required
                  placeholder="First Name"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  className="h-11"
                />
                <Input
                  placeholder="Last Name"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  className="h-11"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1 h-11 rounded-md border border-slate-200 flex items-center justify-center text-slate-700 font-medium bg-slate-50">
                  +91
                </div>
                <Input
                  required
                  type="tel"
                  placeholder="000-000-0000"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="col-span-2 h-11"
                />
              </div>
              <Input
                type="email"
                placeholder="Email ID"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-11"
              />
              <select
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                className="flex w-full h-11 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-colors"
                required
              >
                <option value="">Select State</option>
                {states.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                value={form.bill}
                onChange={(e) => setForm({ ...form, bill: e.target.value })}
                className="flex w-full h-11 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-colors"
              >
                <option value="">Monthly Electricity Bill</option>
                {bills.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>

              <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 accent-orange-500"
                />
                <span>I agree to be contacted by {co.name} via call/WhatsApp regarding my solar enquiry.</span>
              </label>

              <Button
                type="submit"
                disabled={!agree || submitting}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white h-12 font-bold text-base"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                  </span>
                ) : (
                  "Submit"
                )}
              </Button>
              {error && (
                <div className="text-center text-xs text-red-600 -mt-1">{error}</div>
              )}
              <div className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5" /> Your information is safe with us.
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
